import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, Mail, RefreshCw, RotateCcw, Split, Tag, Truck, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusDot } from "@/components/orders/status-badge";
import { MetaAttachTrackingDialog, MetaResetShippingDialog, MetaShipmentDialog } from "./meta-shipment-dialog";
import { formatAddress } from "./candidate-group-card";
import { useDissolveMeta, useRemoveMetaMember, useResendMetaEmail, useRetryMetaSync } from "@/hooks/use-meta-orders";
import { cn, formatCurrency, formatDateTime } from "@/lib/utils";
import type { MetaMember, MetaOrder } from "@/services/meta-orders";

const km = (v: number) => formatCurrency(v, "KM");
/** Isti skup kao ATTENTION_STATUSES na serveru (nalog i pack se tada odbijaju). */
const ATTENTION_LABEL: Record<string, string> = {
  cancelled: "otkazana", refunded: "refundirana", failed: "neuspjela", trash: "u smeću",
};
const fromUnix = (s: number) => formatDateTime(new Date(s * 1000));

/** `restoring`: meta je rastavljena, pa se članu vraća originalna poštarina. */
function ShippingState({ m, restoring }: { m: MetaMember; restoring?: boolean }) {
  if (!m.synced) {
    return (
      <span className="text-red-600">
        {restoring ? "povrat poštarine nije ažuriran" : "nije ažurirano"}{m.syncError ? `: ${m.syncError}` : " (čeka)"}
      </span>
    );
  }
  if (restoring) return <span className="text-muted-foreground">poštarina vraćena ({km(m.originalShipping)})</span>;
  if (m.targetShipping < m.originalShipping) {
    return <span className="text-muted-foreground">poštarina skinuta ({km(m.originalShipping)} → {km(m.targetShipping)})</span>;
  }
  if (m.originalShipping === 0) return <span className="text-muted-foreground">bez poštarine</span>;
  return <span className="text-green-700 dark:text-green-500">poštarina zadržana ({km(m.originalShipping)})</span>;
}

function ShipmentBadge({ meta }: { meta: MetaOrder }) {
  if (!meta.shipment) return <Badge variant="outline" className="bg-muted/40">Bez naloga</Badge>;
  if (meta.shipment.status === "pending") {
    return (
      <Badge variant="outline" className="bg-orange-500/10 text-orange-700 border-orange-500/20">
        Nalog na čekanju (timeout)
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/20 font-mono">
      <Truck />
      {meta.shipment.trackingNumber}
    </Badge>
  );
}

function EmailState({ meta }: { meta: MetaOrder }) {
  if (meta.emailError) {
    return <span className="text-red-600">email nije poslan: {meta.emailError}</span>;
  }
  if (meta.emailSentAt) return <span>email poslan {fromUnix(meta.emailSentAt)}</span>;
  return <span>email čeka ažuriranu poštarinu</span>;
}

type Confirm = { kind: "remove"; member: MetaMember } | { kind: "dissolve" } | null;

type Props = { meta: MetaOrder; highlighted?: boolean };

export function MetaOrderCard({ meta, highlighted }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const retry = useRetryMetaSync();
  const removeMember = useRemoveMetaMember();
  const dissolve = useDissolveMeta();
  const resend = useResendMetaEmail();
  const [shipmentOpen, setShipmentOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);

  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlighted]);

  // Rastavljena/spakovana je na listi samo dok povrat poštarine nekom članu nije upisan: tada je dostupno samo "Ponovi ažuriranje".
  const live = meta.status === "active";
  const dissolved = meta.status === "dissolved";
  const active = meta.members.filter((m) => !m.removed);
  const removed = meta.members.filter((m) => m.removed);
  const hasShipment = !!meta.shipment;
  const lockedByShipment = hasShipment ? "Spojena pošiljka ima nalog — prvo uradi Reset naloga" : undefined;
  const confirmPending = removeMember.isPending || dissolve.isPending;
  const attention = active.find((m) => ATTENTION_LABEL[m.status]);
  const attentionReason = !meta.needsAttention ? undefined
    : attention ? `${attention.storeName} #${attention.number} je ${ATTENTION_LABEL[attention.status]} — izbaci je prije kreiranja naloga`
    : "Neka od narudžbi je otkazana — izbaci je prije kreiranja naloga";
  const shipmentBlocked = attentionReason ?? (meta.allSynced ? undefined : "Poštarina nije ažurirana na svim narudžbama");

  async function handleRetry() {
    try {
      const r = await retry.mutateAsync(meta.id);
      if (r.allSynced && r.removedPending === 0) toast.success(`${meta.number}: poštarina ažurirana na svim narudžbama`);
      else toast.warning(`${meta.number}: neke narudžbe i dalje nisu ažurirane`);
    } catch (e: any) {
      toast.error(e?.message || "Ažuriranje nije uspjelo");
    }
  }

  async function handleResend() {
    try {
      const r = await resend.mutateAsync(meta.id);
      if (r.sent) toast.success(`${meta.number}: email poslan`);
      else toast.error(`Email nije poslan${r.error ? `: ${r.error}` : ""}`);
    } catch (e: any) {
      toast.error(e?.message || "Email nije poslan");
    }
  }

  async function handleConfirm() {
    if (!confirm) return;
    const synced = (r: MetaOrder) => r.allSynced && r.removedPending === 0;
    const unsyncedHint = `poštarina nije ažurirana na svim narudžbama — pokušajte "Ponovi ažuriranje" na ${meta.number}`;
    try {
      if (confirm.kind === "remove") {
        const m = confirm.member;
        const r = await removeMember.mutateAsync({ id: meta.id, storeId: m.storeId, orderId: m.orderId });
        const done = r.status === "dissolved"
          ? `${m.storeName} #${m.number} izbačena — ${meta.number} je rastavljena`
          : `${m.storeName} #${m.number} izbačena iz ${meta.number}`;
        if (synced(r)) toast.success(done);
        else toast.warning(`${done}, ali ${unsyncedHint}`);
      } else {
        const r = await dissolve.mutateAsync(meta.id);
        if (synced(r)) toast.success(`${meta.number} rastavljena — narudžbe su opet samostalne`);
        else toast.warning(`${meta.number} rastavljena, ali ${unsyncedHint}`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Akcija nije uspjela");
    } finally {
      setConfirm(null);
    }
  }

  return (
    <div
      ref={ref}
      id={`meta-${meta.id}`}
      className={cn(
        "ring-1 ring-foreground/10 bg-card scroll-mt-4",
        (meta.needsAttention || dissolved) && "ring-red-500/40",
        highlighted && "ring-2 ring-primary",
      )}
    >
      {/* Zaglavlje */}
      <div className="flex flex-col gap-1.5 px-3 py-2.5 border-b border-foreground/10">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">
            {meta.number} · {meta.storeNames.join(" + ")}
          </span>
          {meta.needsAttention && (
            <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-500/20">
              <AlertTriangle />
              Treba pažnju
            </Badge>
          )}
          {dissolved && (
            <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-500/20">
              <AlertTriangle />
              Rastavljena — povrat poštarine nije ažuriran u WooCommerceu
            </Badge>
          )}
          {meta.status === "packed" && <Badge variant="outline" className="bg-muted/40">Spakovana</Badge>}
          <div className="flex items-center gap-1.5 flex-wrap ml-auto">
            {!dissolved && <ShipmentBadge meta={meta} />}
            {meta.labelPrintedAt && (
              <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/20" title={fromUnix(meta.labelPrintedAt)}>
                <Tag />
                Naljepnica štampana
              </Badge>
            )}
          </div>
        </div>
        <span className="text-xs text-muted-foreground">{formatAddress(meta.address)}</span>
      </div>

      {/* Članovi */}
      <div className="flex flex-col">
        {active.map((m) => (
          <div key={`${m.storeId}:${m.orderId}`} className="flex items-center gap-x-3 gap-y-1 flex-wrap px-3 py-1.5 border-b border-foreground/5 text-xs">
            <span className="font-medium whitespace-nowrap">{m.storeName} #{m.number}</span>
            <StatusDot status={m.status} />
            <span className="tabular-nums whitespace-nowrap">{km(m.total)}</span>
            <span className="whitespace-nowrap">
              {m.isPaid ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/20">Plaćeno</Badge>
              ) : (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/20">Pouzeće</Badge>
              )}
            </span>
            <span className="min-w-0 basis-full sm:basis-0 sm:flex-1"><ShippingState m={m} restoring={dissolved} /></span>
            {live && (
              <Button
                size="xs"
                variant="ghost"
                className="ml-auto text-muted-foreground hover:text-red-600"
                disabled={hasShipment || confirmPending}
                title={lockedByShipment ?? "Izbaci narudžbu iz spojene pošiljke i vrati joj poštarinu"}
                onClick={() => setConfirm({ kind: "remove", member: m })}
              >
                <UserMinus />
                Izbaci
              </Button>
            )}
          </div>
        ))}
        {removed.map((m) => (
          <div key={`${m.storeId}:${m.orderId}`} className="flex items-center gap-x-3 flex-wrap px-3 py-1.5 border-b border-foreground/5 text-xs text-muted-foreground">
            <span className="line-through whitespace-nowrap">{m.storeName} #{m.number}</span>
            <span>izbačena{m.synced ? " — poštarina vraćena" : ""}</span>
            {!m.synced && <span className="text-red-600">povrat poštarine nije ažuriran{m.syncError ? `: ${m.syncError}` : ""}</span>}
          </div>
        ))}
      </div>

      {/* Stanje i akcije */}
      <div className="flex flex-col gap-2 px-3 py-2.5 bg-muted/25">
        {dissolved ? (
          <p className="text-xs text-red-600">
            Narudžbama čiji povrat nije ažuriran kupac bi platio premalo — ne mogu dobiti zaseban nalog dok "Ponovi ažuriranje" ne uspije.
          </p>
        ) : (
          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-muted-foreground">
            <span>
              Otkupnina: <b className="text-foreground tabular-nums">{km(meta.codAmount)}</b>
            </span>
            {meta.shipment?.credentialName && <span>{meta.shipment.credentialName}</span>}
            <span className="inline-flex items-center gap-1.5"><Mail className="size-3" /><EmailState meta={meta} /></span>
          </div>
        )}
        {!hasShipment && attentionReason && (
          <p className="text-xs text-red-600">{attentionReason}</p>
        )}
        {live && meta.shipment?.status === "pending" && (
          <p className="text-xs text-orange-700">
            Kurir nije odgovorio na vrijeme — pošiljka je možda kreirana. Provjerite portal kurira pa unesite tracking ili uradite Reset naloga.
          </p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          {live && !hasShipment && (
            <Button
              size="sm"
              disabled={!!shipmentBlocked}
              title={shipmentBlocked}
              onClick={() => setShipmentOpen(true)}
            >
              <Truck />
              Kreiraj nalog
            </Button>
          )}
          {(!meta.allSynced || meta.removedPending > 0) && (
            <Button size="sm" variant="outline" disabled={retry.isPending} onClick={handleRetry}>
              {retry.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              Ponovi ažuriranje
            </Button>
          )}
          {live && meta.shipment?.status === "pending" && (
            <Button size="sm" variant="outline" onClick={() => setAttachOpen(true)}>
              Unesi tracking
            </Button>
          )}
          {live && hasShipment && (
            <Button size="sm" variant="outline" onClick={() => setResetOpen(true)}>
              <RotateCcw />
              Reset naloga
            </Button>
          )}
          {live && (
            <Button size="sm" variant="outline" disabled={resend.isPending} onClick={handleResend}>
              {resend.isPending ? <Loader2 className="animate-spin" /> : <Mail />}
              Pošalji email ponovo
            </Button>
          )}
          {live && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto text-muted-foreground hover:text-red-600"
              disabled={hasShipment || confirmPending}
              title={lockedByShipment ?? "Vrati sve narudžbe na original"}
              onClick={() => setConfirm({ kind: "dissolve" })}
            >
              <Split />
              Rastavi
            </Button>
          )}
        </div>
      </div>

      <MetaShipmentDialog meta={meta} open={shipmentOpen} onOpenChange={setShipmentOpen} />
      <MetaAttachTrackingDialog meta={meta} open={attachOpen} onOpenChange={setAttachOpen} />
      <MetaResetShippingDialog meta={meta} open={resetOpen} onOpenChange={setResetOpen} />

      <AlertDialog open={confirm !== null} onOpenChange={(v) => { if (!v && !confirmPending) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "remove"
                ? `Izbaciti ${confirm.member.storeName} #${confirm.member.number} iz ${meta.number}?`
                : `Rastaviti ${meta.number}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "remove"
                ? active.length - 1 >= 2
                  ? "Narudžba dobija nazad originalnu poštarinu u WooCommerceu, a poštarina se ponovo računa za preostale narudžbe."
                  : `Ostaje samo jedna narudžba, pa se ${meta.number} ukida i obje narudžbe dobijaju nazad originalnu poštarinu.`
                : "Sve narudžbe dobijaju nazad originalnu poštarinu u WooCommerceu i ponovo se šalju zasebno."}{" "}
              Kupcu se email ne šalje automatski.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirmPending}>Odustani</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmPending}
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                void handleConfirm();
              }}
            >
              {confirmPending && <Loader2 className="size-4 animate-spin" />}
              {confirm?.kind === "remove" ? "Izbaci" : "Rastavi"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
